export const WALLET_VIEWS = {
  DEPOSIT: "deposit",
  WITHDRAW: "withdraw",
  POINT: "point",
  COMP: "comp",
};

export const QUICK_AMOUNTS = [10000, 50000, 100000, 500000, 1000000];
export const HISTORY_LIMIT = 10;

export const MONEY_TYPES = {
  GENERAL: "general",
  HOLDEM: "holdem",
  USDT: "usdt",
};

const getByPath = (source, path) => {
  if (!source || !path) return undefined;
  return path.split(".").reduce((target, key) => {
    if (target && Object.prototype.hasOwnProperty.call(target, key)) {
      return target[key];
    }
    return undefined;
  }, source);
};

export const firstValue = (source, paths = []) => {
  for (const path of paths) {
    const value = getByPath(source, path);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

export const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const normalized = String(value).replace(/[^\d.-]/g, "");
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const formatMoney = (value) => toNumber(value).toLocaleString("ko-KR");

export const formatAmountInput = (value) => {
  const numeric = toNumber(value);
  return numeric > 0 ? formatMoney(numeric) : "";
};

export const formatPoint = (value) => `${formatMoney(value)} P`;

export const formatUsdt = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "0.000000";
  return numeric.toFixed(6);
};

export const formatKrwToUsdt = (amount, rate) => {
  const numericAmount = toNumber(amount);
  const numericRate = toNumber(rate);
  if (!numericAmount || numericRate <= 0) return "0.000000";
  return formatUsdt(numericAmount / numericRate);
};

export const getProfileBalance = (profile, type = "money") => {
  const paths = {
    money: [
      "wallet.available_balance",
      "wallet.balance",
      "available_balance",
      "balance",
      "money",
      "user_money",
    ],
    point: [
      "wallet.point_balance",
      "point_balance",
      "points",
      "point",
      "user_point",
    ],
    comp: [
      "wallet.rolling_balance",
      "wallet.comp_balance",
      "rolling_balance",
      "comp_balance",
      "comp",
      "rolling",
      "user_rolling",
    ],
    holdemMoney: [
      "wallet.holdem_balance",
      "wallet.holdem_money",
      "holdem_balance",
      "holdem_money",
    ],
    holdemComp: [
      "wallet.holdem_rolling_balance",
      "wallet.holdem_comp_balance",
      "wallet.holdem_comp",
      "holdem_rolling_balance",
      "holdem_comp_balance",
      "holdem_comp",
    ],
  };

  return toNumber(firstValue(profile, paths[type] || paths.money));
};

export const isAnonymousProfile = (profile = {}) => profile?.signup_type === "ANONYMOUS";

export const isUsdtDepositEnabled = (settings = {}) =>
  Boolean(
    settings?.crypto_enabled ??
      settings?.whether_to_use_crypto ??
      settings?.cryptoEnabled ??
      settings?.whether_to_use_usdt
  );

export const isUsdtWalletInfoEnabled = (settings = {}) =>
  Boolean(settings?.whether_to_use_usdt ?? settings?.crypto_enabled ?? settings?.whether_to_use_crypto);

export const getMoneyTypeTabs = (settings = {}, { includeUsdt = false } = {}) => {
  const tabs = [{ key: MONEY_TYPES.GENERAL, label: "일반" }];
  if (settings?.holdem_enabled) tabs.push({ key: MONEY_TYPES.HOLDEM, label: "홀덤" });
  if (includeUsdt && isUsdtDepositEnabled(settings)) tabs.push({ key: MONEY_TYPES.USDT, label: "USDT" });
  return tabs;
};

export const getSelectedBank = (bankRows = [], profile = {}) => {
  const bankId = firstValue(profile, ["user_info.bank_id", "bank_id", "user_info.bank.id", "bank.id"]);
  if (!bankId) return null;
  return bankRows.find((bank) => String(bank?.id) === String(bankId)) || null;
};

export const getProfileDepositorName = (profile = {}) =>
  firstValue(profile, [
    "depositor_name",
    "user_info.depositor_name",
    "account_holder",
    "user_info.account_holder",
    "bank_account_holder",
  ]);

export const getProfileAccountNumber = (profile = {}) =>
  firstValue(profile, [
    "user_info.account_no",
    "user_info.account_number",
    "account_no",
    "account_number",
    "bank_account_number",
  ]);

export const getProfileUsdtAddress = (profile = {}) =>
  firstValue(profile, [
    "user_info.usdt_wallet_address",
    "usdt_wallet_address",
    "wallet.usdt_wallet_address",
    "crypto_wallet_address",
  ]);

export const maskAccountNumber = (accountNo) => {
  if (!accountNo) return "-";
  const value = String(accountNo).trim();
  if (!value) return "-";
  if (value.length <= 4) return "*".repeat(value.length);
  return `${value.slice(0, 2)}${"*".repeat(value.length - 4)}${value.slice(-2)}`;
};

export const getCryptoWalletAddress = (wallet = {}) =>
  firstValue(wallet, [
    "address",
    "wallet_address",
    "usdt_wallet_address",
    "walletAddress",
    "crypto_wallet_address",
    "data.address",
    "data.wallet_address",
  ]);

export const getCryptoMinDeposit = (wallet = {}) =>
  toNumber(firstValue(wallet, ["min_deposit", "minimum_deposit", "minDeposit", "data.min_deposit"]));

export const getUsdtRate = (settings = {}, cryptoRate = {}) =>
  toNumber(
    firstValue(cryptoRate, ["rate", "exchange_rate", "usdt_exchange_rate", "data.rate"]) ||
      firstValue(settings, ["usdt_exchange_rate", "crypto_rate", "usdt_rate"])
  );

export const getHeadquartersUsdtAddress = (settings = {}) =>
  firstValue(settings, [
    "usdt_headquarters_wallet_address",
    "usdt_company_wallet_address",
    "usdt_wallet_address",
    "headquarters_usdt_wallet_address",
    "crypto_wallet_address",
  ]);

export const getResponseRows = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.data?.rows)) return data.data.rows;
  if (Array.isArray(data.payload?.rows)) return data.payload.rows;
  return [];
};

export const getResponseTotal = (data, fallbackRows = []) => {
  if (!data) return fallbackRows.length || 0;
  const total = firstValue(data, [
    "count",
    "total",
    "totalCount",
    "total_count",
    "pagination.total",
    "meta.total",
    "data.count",
    "data.total",
    "payload.count",
    "payload.total",
  ]);
  const numeric = toNumber(total);
  return numeric > 0 ? numeric : fallbackRows.length || 0;
};

export const isSuccessResult = (result) =>
  Boolean(result && result.success !== false && result.status !== false && !result.error);

export const getResultMessage = (result, fallback = "요청 처리 중 문제가 발생했습니다.") =>
  result?.message || result?.error || fallback;

const formatDateValue = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return `${month}-${day} ${hour}:${minute}`;
};

export const formatDate = (row) =>
  formatDateValue(
    row?.created_at ||
      row?.createdAt ||
      row?.regdate ||
      row?.requested_at ||
      row?.updated_at ||
      row?.date ||
      row?.time
  );

export const formatCryptoDepositDate = (row) =>
  formatDateValue(row?.charged_at || row?.chargedAt || row?.created_at || row?.createdAt);

export const shortenTransactionHash = (value, headLength = 8, tailLength = 6) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  if (normalized.length <= headLength + tailLength + 1) return normalized;
  return `${normalized.slice(0, headLength)}…${normalized.slice(-tailLength)}`;
};

export const getHistoryAmount = (row) =>
  toNumber(
    row?.amount ??
      row?.usdt_amount ??
      row?.amount_usdt ??
      row?.deposit_amount ??
      row?.krw_amount ??
      row?.money ??
      row?.request_amount ??
      row?.change_amount ??
      row?.point ??
      row?.rolling_amount ??
      row?.value
  );

export const getHistoryStatus = (row) => {
  const raw = String(row?.status ?? row?.state ?? row?.type ?? row?.kind ?? "").toLowerCase();

  if (raw.includes("approve") || raw.includes("complete") || raw === "success" || raw === "done") {
    return { label: "완료", className: "is-complete" };
  }
  if (raw.includes("reject") || raw.includes("cancel") || raw.includes("fail")) {
    return { label: "취소", className: "is-cancelled" };
  }
  if (raw === "credit") return { label: "입금", className: "is-complete" };
  if (raw === "debit") return { label: "출금", className: "is-pending" };
  if (raw.includes("pending") || raw.includes("wait")) return { label: "대기", className: "is-pending" };
  return { label: "대기", className: "is-pending" };
};

export const getCryptoDepositStatus = (row) => {
  const raw = String(row?.status || "").toLowerCase();

  if (raw === "approved" || raw === "complete" || raw === "completed") {
    return { label: "충전완료", className: "is-complete" };
  }
  if (raw === "confirmed" || raw === "confirming") {
    return { label: "확인중", className: "is-pending" };
  }
  if (raw === "rejected" || raw === "failed" || raw === "cancelled") {
    return { label: "거부", className: "is-cancelled" };
  }
  return { label: "대기중", className: "is-pending" };
};

export const maskUserName = (row) =>
  row?.user_unique_id ||
  row?.user_id ||
  row?.nickname ||
  row?.nick_name ||
  row?.username ||
  row?.name ||
  "-";

export const getBonusTitle = (bonus) => {
  const title = [
    bonus?.bonus_name,
    bonus?.title,
    bonus?.name,
    bonus?.bonus_title,
    bonus?.label,
  ]
    .filter(Boolean)
    .shift();
  const detail = bonus?.bonus_content || bonus?.content || bonus?.description;
  if (title && detail) return `${title} - ${detail}`;
  if (title) return title;
  return `${formatMoney(bonus?.percent || bonus?.rate || 0)}% 보너스`;
};

export const getBonusId = (bonus) => bonus?.id || bonus?._id || bonus?.bonus_id || bonus?.value;

export const getWalletNoticeHtml = (settings = {}, profile = {}, view) => {
  const isAnonymous = profile?.signup_type === "ANONYMOUS";

  if (view === WALLET_VIEWS.DEPOSIT) {
    return isAnonymous ? settings?.anonymous_deposit_notes : settings?.deposit_notes;
  }
  if (view === WALLET_VIEWS.WITHDRAW) {
    return isAnonymous ? settings?.anonymous_withdrawal_notes : settings?.withdrawal_notes;
  }
  if (view === WALLET_VIEWS.POINT) {
    return settings?.point_notes;
  }
  if (view === WALLET_VIEWS.COMP) {
    return settings?.rolling_notes || settings?.comp_notes;
  }

  return "";
};
